import React,{useState,useEffect,useRef,useCallback}from'react';
import api from'../../services/api';

interface Props{
  label?:string;
  value?:string;       // doctorId selected
  displayValue?:string;// doctor name to show
  onSelect:(doctor:any)=>void;
  onClear?:()=>void;
  placeholder?:string;
  required?:boolean;
  className?:string;
  filterSpecialization?:string;
}

const INP='w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400';
const LBL='block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5';

export default function DoctorSearchInput({label,value,displayValue,onSelect,onClear,placeholder='Search doctor by name or specialization...',required,className='',filterSpecialization}:Props){
  const[q,setQ]=useState(displayValue||'');
  const[results,setResults]=useState<any[]>([]);
  const[loading,setLoading]=useState(false);
  const[show,setShow]=useState(false);
  const[recent,setRecent]=useState<any[]>([]);
  const ref=useRef<HTMLDivElement>(null);

  // Load recent doctors on mount
  useEffect(()=>{
    api.getDoctors?.({take:8,isActive:true}).then((r:any)=>{
      const list=r?.data?.data||r?.data?.doctors||r?.data||(Array.isArray(r?.data)?r.data:[]);
      const arr=Array.isArray(list)?list:[];
      setRecent(filterSpecialization?arr.filter((d:any)=>(d.specialization||'').toLowerCase().includes(filterSpecialization.toLowerCase())):arr);
    }).catch(()=>{});
  },[filterSpecialization]);

  // Close on outside click
  useEffect(()=>{
    const h=(e:MouseEvent)=>{if(ref.current&&!ref.current.contains(e.target as Node))setShow(false);};
    document.addEventListener('mousedown',h);return()=>document.removeEventListener('mousedown',h);
  },[]);

  // Debounced search
  useEffect(()=>{
    if(q.length<2){setResults([]);return;}
    const t=setTimeout(async()=>{
      setLoading(true);
      try{
        const r=await api.getDoctors?.({search:q,take:10,isActive:true});
        const list=r?.data?.data||r?.data?.doctors||r?.data||(Array.isArray(r?.data)?r.data:[]);
        const arr=Array.isArray(list)?list:[];
        setResults(filterSpecialization?arr.filter((d:any)=>(d.specialization||'').toLowerCase().includes(filterSpecialization.toLowerCase())):arr);
        setShow(true);
      }catch{setResults([]);}
      setLoading(false);
    },300);
    return()=>clearTimeout(t);
  },[q,filterSpecialization]);

  const pick=(d:any)=>{
    const name=`Dr. ${d.firstName||d.first_name||''} ${d.lastName||d.last_name||''}`.trim();
    setQ(name);setResults([]);setShow(false);
    onSelect(d);
  };

  const clear=()=>{
    setQ('');setResults([]);setShow(false);
    onClear?.();
  };

  const getName=(d:any)=>`${d.firstName||d.first_name||''} ${d.lastName||d.last_name||''}`.trim();
  const display=q.length>=2?results:recent;

  return(
    <div className={className}>
      {label&&<label className={LBL}>{label}{required&&<span className="text-red-500 ml-0.5">*</span>}</label>}
      <div ref={ref} className="relative" style={{isolation:'isolate'}}>
        <div className="relative">
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">👨‍⚕️</span>
          <input
            value={q} onFocus={()=>setShow(true)}
            onChange={e=>{setQ(e.target.value);if(!e.target.value)onClear?.();}}
            placeholder={placeholder} required={required}
            className={`${INP} pl-10 pr-10`}
          />
          {loading&&<div className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-[#02357A]/20 border-t-[#02357A] rounded-full animate-spin"/>}
          {!loading&&q&&<button type="button" onClick={clear} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 text-xl leading-none">×</button>}
        </div>

        {show&&(
          <div className="absolute z-[9999] top-full left-0 right-0 mt-1.5 bg-white border-2 border-slate-200 rounded-2xl shadow-2xl max-h-72 overflow-y-auto"
            style={{boxShadow:'0 25px 50px -12px rgba(0,0,0,0.3)'}}>
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                {q.length<2?'👨‍⚕️ Doctors':'🔍 Results'}
              </span>
              <span className="text-xs text-slate-300">{q.length<2?'Type to search':display.length+' found'}</span>
            </div>

            {display.length>0?display.map((d:any)=>{
              const name=getName(d);
              const spec=d.specialization||d.department||'';
              const dept=d.departmentName||d.department_name||'';
              return(
                <div key={d.id} onClick={()=>pick(d)}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-0 transition-colors group">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#02357A] to-[#039089] flex items-center justify-center text-white font-black text-sm flex-shrink-0">
                    {(name||'D')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-slate-800 group-hover:text-[#02357A] transition-colors">Dr. {name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{spec}{dept&&spec?' · ':''}{dept}</p>
                  </div>
                  {d.isAvailable!==false&&<span className="text-xs text-emerald-600 font-semibold flex-shrink-0">Available</span>}
                </div>
              );
            }):(
              q.length>=2&&!loading&&(
                <div className="p-6 text-center">
                  <p className="text-sm text-slate-400">No doctors found for "{q}"</p>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}
